import React, { useState, useEffect } from "react";
import Card from "../../shared/Card";
import { getScoreGradient } from "../../../utils/colorUtils";
import { HealthScore, RiskExposureChart } from "./HealthAndRiskMetricsDetails";
import api from "../../../utils/apiConfig";
import RiskQuestionnaireWizard from "./RiskQuestionnaireWizard";
import ShareQuestionnaireModal from "./ShareQuestionnaireModal";
import { useTheme } from '../../../contexts/ThemeContext';

import logger from '../../../utils/logger';

const RiskProfileCard = ({ householdId, userId }) => {
  const { theme } = useTheme();
  const [showSummary, setShowSummary] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [riskData, setRiskData] = useState({
    desiredRiskNumber: 0,
    currentRiskNumber: 0,
  });
  const [householdMembers, setHouseholdMembers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch risk data
        const riskEndpoint = userId
          ? `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/risk-tolerance-analysis`
          : `/advisor-portal/client-database/household-profile/${householdId}/risk-tolerance-analysis`;

        // First fetch risk data
        const riskResponse = await api.get(riskEndpoint);
        setRiskData(riskResponse.data);

        // Then fetch household data which includes members
        try {
          const householdResponse = await api.get(
            `/advisor-portal/client-database/household-profile/${householdId}`,
          );
          logger.info("Household Response:", householdResponse.data);

          if (
            householdResponse.data &&
            Array.isArray(householdResponse.data.members)
          ) {
            const formattedMembers = await Promise.all(
              householdResponse.data.members.map(async (member) => {
                let email = null;
                try {
                  const userId = member.id || member.userId || member.user_id;
                  // Use the household-scoped endpoint for user data
                  const userResponse = await api.get(
                    `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
                  );
                  logger.info("User data for member:", userResponse.data);

                  // Get email directly from user data
                  email = userResponse.data?.email;
                } catch (error) {
                  logger.warn(
                    `Failed to fetch data for member ${member.id}:`,
                    error,
                  );
                }

                return {
                  id: member.id || member.userId || member.user_id,
                  name:
                    member.name ||
                    `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim(),
                  email: email,
                };
              }),
            );

            logger.info("Formatted members with email:", formattedMembers);
            setHouseholdMembers(formattedMembers);
          } else {
            logger.warn(
              "No members array in response:",
              householdResponse.data,
            );
            setHouseholdMembers([]);
          }
        } catch (membersError) {
          logger.error("Error fetching household members:", membersError);
          setHouseholdMembers([]);
        }

        setLoading(false);
      } catch (err) {
        logger.error("Error fetching data:", err);
        setError("Failed to load risk data");
        setLoading(false);
      }
    };

    if (householdId) {
      fetchData();
    }
  }, [householdId, userId]);

  const handleCardClick = () => {
    setShowSummary(true);
  };

  const handleBackToSummary = () => {
    setShowQuestionnaire(false);
    setShowShareModal(false);
    setShowSummary(true);
  };

  const handleTakeQuestionnaire = () => {
    setShowSummary(false);
    setShowQuestionnaire(true);
  };

  const handleShareQuestionnaire = () => {
    setShowSummary(false);
    setShowShareModal(true);
  };

  const handleSave = async (formData) => {
    try {
      setLoading(true);
      const endpoint = userId
        ? `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/risk-tolerance-analysis`
        : `/advisor-portal/client-database/household-profile/${householdId}/risk-tolerance-analysis`;

      const response = await api.post(endpoint, formData);
      setRiskData(response.data);
      setShowQuestionnaire(false);
    } catch (err) {
      logger.error("Error saving risk data:", err);
      setError("Failed to save risk data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden pb-0">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="relative overflow-hidden pb-0">
        <div className="flex items-center justify-center h-full text-red-600">
          {error}
        </div>
      </Card>
    );
  }

  const riskDiff = riskData.currentRiskNumber - riskData.desiredRiskNumber;
  const alignmentScore =
    riskDiff === 0
      ? 100
      : Math.round(
          (1 - Math.abs(riskDiff) / (riskData.desiredRiskNumber || 1)) * 100,
        );
  const gradientColors = getScoreGradient(alignmentScore);

  return (
    <>
      <Card
        className="relative overflow-hidden pb-0 hover:scale-[1.03] hover:shadow-xl dark:shadow-2xl dark:shadow-black/20 active:scale-95 transition-all duration-200 cursor-pointer"
        onClick={handleCardClick}
      >
        {/* Background Gradient */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${gradientColors.background} opacity-50 dark:opacity-0`}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br dark:from-gray-800 dark:to-gray-800/95"></div>

        <div className="relative h-full flex flex-col">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center mt-0">
              <svg
                className={`w-5 h-5 ${gradientColors.text} mr-2`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 10-2 0 1 1 0 002 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              Risk Profile
            </h3>
            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200">
              <svg
                className="w-5 h-5 text-gray-500 dark:text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
          </div>

          {/* Risk Score Display */}
          <div className="flex justify-between items-center my-6">
            <div className="transform transition-transform duration-200 hover:scale-105">
              <div className="text-center mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Desired Risk
                </span>
              </div>
              <RiskExposureChart riskScore={riskData.desiredRiskNumber} />
            </div>
            <div className="transform transition-transform duration-200 hover:scale-105">
              <div className="text-center mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Current Risk
                </span>
              </div>
              <RiskExposureChart riskScore={riskData.currentRiskNumber} />
            </div>
          </div>

          <div className="mt-4 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Risk Alignment
              </h4>
              <span
                className={`text-sm font-medium ${riskDiff > 0 ? "text-red-600 dark:text-red-400" : riskDiff < 0 ? "text-orange-600 dark:text-orange-400" : "text-green-600 dark:text-green-400"}`}
              >
                {riskDiff === 0
                  ? "(100%)"
                  : `(${riskDiff > 0 ? "+" : ""}${riskDiff})`}
              </span>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <li className="flex items-center">
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${gradientColors.progress}`}
                ></span>
                Portfolio Alignment: {alignmentScore}%
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                Interest Rate Risk
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                Liquidity Risk
              </li>
            </ul>
          </div>
        </div>

        {/* Progress Bar at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full ${gradientColors.progress} transition-all duration-500 ease-out`}
            style={{ width: `${alignmentScore}%` }}
          ></div>
        </div>
      </Card>

      {/* Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[60]">
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-5 border border-gray-100 dark:border-gray-700 w-full max-w-4xl shadow-lg dark:shadow-2xl dark:shadow-black/20 rounded-md bg-white dark:bg-gray-900">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Risk Profile Summary
              </h3>
              <button
                onClick={() => setShowSummary(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
              >
                <svg
                  className="w-5 h-5 text-gray-500 dark:text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Risk Distribution
                    </h4>
                    <div className="aspect-w-16 aspect-h-9">
                      <div className="w-full h-full bg-white dark:bg-gray-900 rounded-lg flex items-center justify-center text-gray-900 dark:text-gray-100">
                        <RiskExposureChart
                          riskScore={riskData.currentRiskNumber}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Historical Risk Trends
                    </h4>
                    <div className="aspect-w-16 aspect-h-9">
                      <div className="w-full h-full bg-white dark:bg-gray-900 rounded-lg flex items-center justify-center text-gray-900 dark:text-gray-100">
                        Risk History Chart
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Risk Factors Analysis
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-center justify-between text-gray-900 dark:text-gray-100">
                        <span>Market Risk</span>
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500"
                            style={{ width: "75%" }}
                          ></div>
                        </div>
                      </li>
                      <li className="flex items-center justify-between text-gray-900 dark:text-gray-100">
                        <span>Credit Risk</span>
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500"
                            style={{ width: "60%" }}
                          ></div>
                        </div>
                      </li>
                      <li className="flex items-center justify-between text-gray-900 dark:text-gray-100">
                        <span>Liquidity Risk</span>
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-500"
                            style={{ width: "45%" }}
                          ></div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Recommendations
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-900 dark:text-gray-100">
                      <li className="flex items-start">
                        <span className="w-2 h-2 mt-1.5 rounded-full bg-green-500 mr-2"></span>
                        <span>
                          Consider rebalancing portfolio to align with target
                          risk profile
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 mt-1.5 rounded-full bg-yellow-500 mr-2"></span>
                        <span>
                          Review hedging strategies for interest rate exposure
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 mr-2"></span>
                        <span>
                          Evaluate alternative investments to diversify risk
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleTakeQuestionnaire}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  Take Questionnaire
                </button>
                <button
                  onClick={handleShareQuestionnaire}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  Share Questionnaire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RiskQuestionnaireWizard
        isOpen={showQuestionnaire}
        onClose={handleBackToSummary}
        onComplete={handleSave}
        initialData={{
          totalInvestableAssets: "",
          devastatingLossAmount: "",
          isHouseholdResponse: false,
          primaryFinancialGoal: "",
          customGoal: "",
          timeHorizon: "",
          desiredRiskNumber: riskData.desiredRiskNumber,
          currentRiskNumber: riskData.currentRiskNumber,
        }}
      />

      <ShareQuestionnaireModal
        isOpen={showShareModal}
        onClose={handleBackToSummary}
        householdMembers={householdMembers}
        householdId={householdId}
      />
    </>
  );
};

export default RiskProfileCard;
